import { Assets, Texture, TextureSource } from 'pixi.js';
import { DECOR_ANIM, decorAnimUrls } from './decor-anim';

// Pixel-art: never smooth textures.
TextureSource.defaultOptions.scaleMode = 'nearest';

// Every map sprite is 16px-native art from the Cute Fantasy pack, cropped to
// content, so ONE uniform scale (TILE / 16) sizes them all correctly on screen.
export const NATIVE_PX = 16;

// Tree species-sizes; each has an `_a` + `_b` full-tree variant and a `_stump`.
// birch = autumn-gold birches, re-sliced clean from the pack (Big/Medium sheets);
// `birch_big` are the tall landmark birches that anchor the birch grove.
export const TREE_KINDS = ['oak_big', 'oak', 'spruce_big', 'spruce', 'birch_big', 'birch'] as const;
export type TreeKind = (typeof TREE_KINDS)[number];

// `grass_field` = a 64px (4×4-tile) grass texture with sparse blade/pebble detail
// baked in, so the lawn tiles with a 4-tile period instead of one dead-flat cell.
// 'dirt' was loaded but never rendered anywhere (terrain is grass/water/till only) —
// dropped so we don't fetch a dead texture. Re-add if a dirt-path feature lands.
const GROUND_KEYS = ['grass', 'grass_field', 'volcano_field', 'soil', 'soil_wet'];

// Small ground decor: on-disk file base → how many variants (files <base>_<i>.png).
// The `grass_tuft` decor kind is stored on disk as `grass_*`.
export const DECOR_FILE: Record<string, string> = {
  flowers: 'flower', mushrooms: 'mushroom', rock: 'rock', grass_tuft: 'grass', bush: 'bush',
  // The boulder moved out of TERRAIN_DECOR (which is one texture per kind) because
  // it now has four sprites: the pack's Rock_11..14 outcrops. The old single
  // terrain/boulder.png WAS Rock_12, so boulder_1 is pixel-identical to it and the
  // existing 1.6 decorScale still frames these correctly.
  boulder: 'boulder',
  // structures + extra ground decor (file base = kind, except a few above)
  well: 'well', signpost: 'signpost', scarecrow: 'scarecrow', fence: 'fence', barrel: 'barrel',
  lantern: 'lantern', logs: 'logs', pebbles: 'pebbles', flowerpot: 'flowerpot', nest: 'nest', haybale: 'haybale',
  basalt_pillar: 'basalt_pillar', basalt_boulder: 'basalt_boulder', volcano_tower: 'volcano_tower',   // volcano biome
  fountain: 'fountain', oracle: 'oracle',   // birch-grove shrine + quest giver
};
export const DECOR_COUNT: Record<string, number> = {
  // 14 pack rocks: 10 ground rocks + 4 outcrops (scripts/build-rock-decor.py).
  flowers: 5, mushrooms: 3, rock: 10, grass_tuft: 3, bush: 2, boulder: 4,
  well: 1, signpost: 1, scarecrow: 1, fence: 1, barrel: 1,
  lantern: 1, logs: 2, pebbles: 1, flowerpot: 3, nest: 1, haybale: 1,
  basalt_pillar: 1, basalt_boulder: 1, volcano_tower: 1,
  fountain: 1, oracle: 2,   // oracle_1 = Fisherman Fin (scripts/build-fisherman-fin.py)
};

// Grass-topped cliff plateaus. The boulder used to live here as a single texture;
// it is a multi-variant decor sprite now (see DECOR_FILE) and loading the old
// terrain/boulder.png as well would just fetch a texture nothing reads.
export const TERRAIN_DECOR = ['hillock', 'mountain'] as const;

export interface TreeTex { a: Texture; b: Texture; stump: Texture; sapling: Texture }

// Animated base sea (calm, moving) + FX layered on top (background stripped so
// they overlay cleanly): ripple droplets, fish, static stones.
// still = the flat sea the whole map is tiled with. ripple/wave are the animated
// blocks the world gen scatters over it, so movement sits in pockets instead of
// every cell running the same splash.
export const WATER_FX = { still: 1, ripple: 7, wave: 14, fish: 16, stone: 3 } as const;

// Animated waterline autotile (grass or stony edge), drawn on shore-water tiles.
// Edge keys by adjacent land + concave corner keys by diagonal land; 8 frames.
export const SHORE_KEYS = ['tl', 't', 'tr', 'l', 'r', 'bl', 'b', 'br', 'ctl', 'ctr', 'cbl', 'cbr'] as const;
export type ShoreKey = (typeof SHORE_KEYS)[number];
export const SHORE_VARIANTS = ['grass', 'stone'] as const;
export const SHORE_FRAMES = 8;

// Bed connectivity overlays (owner-designed): key = which cardinal sides also
// have soil. Stacked over the single-cell bed so merged beds read as one shape.
export const OVERLAY_KEYS = ['lrtb', 'lrb', 'lrt', 'rtb', 'ltb', 'rb', 'lb', 'rt', 'lt', 'lr', 'r', 'l', 'tb', 'b', 't'] as const;
export type OverlayKey = (typeof OVERLAY_KEYS)[number];

// Grass pockets inside merged beds: 1-cell hole, 1-wide channels, channel dead
// ends (caps) and channel openings into the lawn (mouths).
export const INNER_KEYS = ['hole', 'ch_v', 'ch_h', 'cap_t', 'cap_b', 'cap_l', 'cap_r', 'mouth_t', 'mouth_b', 'mouth_l', 'mouth_r'] as const;
export type InnerKey = (typeof INNER_KEYS)[number];

export interface MapTextures {
  ground: Record<string, Texture>;                 // grass, soil, soil_wet
  tree: Record<TreeKind, TreeTex>;                 // full-tree a/b + stump per species-size
  decor: Record<string, Texture[]>;                // decor[kind] = variant textures
  decorAnim: Record<string, Texture[][]>;          // decorAnim[kind][variant] = frames (map/decor-anim.ts)
  terrain: Record<string, Texture>;                // hillock, mountain (grass-topped cliff mounds)
  fx: Record<string, Texture>;                     // leaf_green, leaf_gold, needle, log_0/1, stump_ground
  water: Record<string, Texture[]>;                // still[], ripple[], wave[], fish[], stone[]
  shore: Record<(typeof SHORE_VARIANTS)[number], Record<ShoreKey, Texture[]>>; // animated waterline autotile
  frame: Record<ShoreKey, Texture>;                // sand-lipped grass ring drawn AROUND a plot cell
  till: { dry: Texture; wet: Texture };            // hoed soil bed (grass-edged), dry + watered
  plot: { dry: Texture; wet: Texture };            // single-cell bed tile (FarmLand 0,0), dry + watered
  overlay: Record<OverlayKey, { dry: Texture; wet: Texture }>; // bed connectivity overlay (merged shapes)
  inner: Record<InnerKey, Texture>;                // grass pockets in beds: hole / channels / caps / mouths
  ring: Texture[];                                 // radial progress bar (red→green), 15 frames
  mining: Record<string, Texture>;                 // rock_ore, rock_gem (nodes), drop_ore, drop_gem, bar_ore, bar_gem
  res: Record<(typeof RES_KEYS)[number], Texture>; // resource ITEM icons (pack Resources sheet): drops + bag
}

// The four per-metal vein nodes joined the two legacy ones: `rock_ore`/`rock_gem`
// are still loaded because the dungeon view and any pre-ore-type saved map still
// reference them.
const MINING_KEYS = ['rock_ore', 'rock_gem', 'drop_ore', 'drop_gem', 'bar_ore', 'bar_gem',
  'rock_copper', 'rock_iron', 'rock_gold', 'rock_mythril',
  // Tiers 7-9. A missing key here is SILENT — decorTexture falls back to
  // rock_ore — so an emerald field would render as plain grey rock and the metal
  // would look like it was never added.
  'rock_emerald', 'rock_amber', 'rock_ruby'];
export const RES_KEYS = ['stone', 'ore', 'gem', 'wood'] as const;

export const RING_FRAMES = 15;

/**
 * Every map/plot sprite URL the loader will request, keyed by texture name.
 * Exported (not private to the loader) so the sprite-existence test can assert
 * the exact set against the filesystem — a missing PNG is invisible in jsdom.
 */
export function mapTextureUrls(): Record<string, string> {
  const u: Record<string, string> = {};
  for (const k of GROUND_KEYS) u[k] = `/sprites/${k}.png`;
  for (const t of TREE_KINDS) {
    u[`tree_${t}_a`] = `/sprites/trees/${t}_a.png`;
    u[`tree_${t}_b`] = `/sprites/trees/${t}_b.png`;
    u[`tree_${t}_stump`] = `/sprites/trees/${t}_stump.png`;
  }
  for (const s of ['oak', 'spruce', 'birch']) u[`sapling_${s}`] = `/sprites/trees/${s}_sapling.png`;
  for (const kind of Object.keys(DECOR_COUNT)) {
    const base = DECOR_FILE[kind];
    for (let i = 0; i < DECOR_COUNT[kind]; i++) u[`decor_${kind}_${i}`] = `/sprites/decor/${base}_${i}.png`;
  }
  // Animated variants of some of those same kinds (grass/flowers/mushrooms/fountain).
  // Loaded ALONGSIDE the stills, not instead of them: the still is what the
  // build-ghost, the minimap and any non-animated draw path still ask for.
  Object.assign(u, decorAnimUrls());
  for (const t of TERRAIN_DECOR) u[`terrain_${t}`] = `/sprites/terrain/${t}.png`;
  u['terrain_dungeon'] = '/sprites/props/dungeon_arch.png';   // Block 6: dungeon entrance
  for (const f of ['leaf_green', 'leaf_gold', 'needle', 'log_0', 'log_1', 'stump_ground'])
    u[`fx_${f}`] = `/sprites/fx/${f}.png`;
  u['fx_axe'] = '/sprites/ui/axe.png';
  // The tool/build target bracket. It was NOT preloaded, and scene.ts built its
  // sprite with Texture.from(url) — which in Pixi 8 does not fetch an unloaded
  // URL, it returns an EMPTY texture. So the focus frame has never rendered at
  // all, which is why the owner reported the feature as missing rather than
  // wrong. Loading it here makes it a real texture like every other sprite.
  u['fx_tile_select'] = '/sprites/ui/tile_select.png';
  for (const [k, n] of Object.entries(WATER_FX))
    for (let i = 0; i < n; i++) u[`water_${k}_${i}`] = `/sprites/water/${k}_${i}.png`;
  for (const v of SHORE_VARIANTS) for (const k of SHORE_KEYS)
    for (let f = 0; f < SHORE_FRAMES; f++) u[`shore_${v}_${k}_${f}`] = `/sprites/water/shore/${v}_${k}_${f}.png`;
  for (const k of SHORE_KEYS) u[`frame_${k}`] = `/sprites/farmland/frame/${k}.png`;
  u['till_dry'] = '/sprites/farmland/till_dry.png';
  u['till_wet'] = '/sprites/farmland/till_wet.png';
  for (const w of ['dry', 'wet']) u[`plot_${w}`] = `/sprites/farmland/plot/bed_${w}.png`;
  for (const k of OVERLAY_KEYS) for (const w of ['dry', 'wet']) u[`overlay_${k}_${w}`] = `/sprites/farmland/overlay/${k}_${w}.png`;
  for (const k of INNER_KEYS) u[`inner_${k}`] = `/sprites/farmland/inner/${k}.png`;
  for (let i = 0; i < RING_FRAMES; i++) u[`ring_${i}`] = `/sprites/ui/ring/ring_${i}.png`;
  for (const m of MINING_KEYS) u[`mining_${m}`] = `/sprites/mining/${m}.png`;
  for (const r of RES_KEYS) u[`res_${r}`] = `/sprites/resources/${r}.png`;
  return u;
}

/** Load every map/plot sprite once. Sprites are pre-cropped PNGs — no in-code slicing. */
export async function loadMapTextures(): Promise<MapTextures> {
  const map = mapTextureUrls();
  const loaded = (await Assets.load(Object.values(map))) as Record<string, Texture>;
  const tex: Record<string, Texture> = {};
  for (const [k, u] of Object.entries(map)) tex[k] = loaded[u] ?? Texture.from(u);

  const ground: Record<string, Texture> = {};
  for (const k of GROUND_KEYS) ground[k] = tex[k];
  const tree = {} as Record<TreeKind, TreeTex>;
  for (const t of TREE_KINDS) {
    const saplingSpecies = t.startsWith('spruce') ? 'spruce' : t.startsWith('birch') ? 'birch' : 'oak';
    const sapling = tex[`sapling_${saplingSpecies}`];
    tree[t] = { a: tex[`tree_${t}_a`], b: tex[`tree_${t}_b`], stump: tex[`tree_${t}_stump`], sapling };
  }

  const decor: Record<string, Texture[]> = {};
  for (const kind of Object.keys(DECOR_COUNT))
    decor[kind] = Array.from({ length: DECOR_COUNT[kind] }, (_, i) => tex[`decor_${kind}_${i}`]);

  // Indexed BY VARIANT (sparse where a kind animates only some variants — the
  // oracle kind animates variant 1, Fisherman Fin, and not variant 0).
  const decorAnim: Record<string, Texture[][]> = {};
  for (const [kind, spec] of Object.entries(DECOR_ANIM)) {
    decorAnim[kind] = [];
    for (const v of spec.variants)
      decorAnim[kind][v] = Array.from({ length: spec.frames }, (_, f) => tex[`decoranim_${kind}_${v}_${f}`]);
  }

  const terrain: Record<string, Texture> = {};
  for (const t of TERRAIN_DECOR) terrain[t] = tex[`terrain_${t}`];
  terrain['dungeon'] = tex['terrain_dungeon'];

  const fx: Record<string, Texture> = {};
  for (const f of ['leaf_green', 'leaf_gold', 'needle', 'log_0', 'log_1', 'stump_ground', 'axe', 'tile_select']) fx[f] = tex[`fx_${f}`];

  const water: Record<string, Texture[]> = {};
  for (const [k, n] of Object.entries(WATER_FX))
    water[k] = Array.from({ length: n }, (_, i) => tex[`water_${k}_${i}`]);

  const shore = {} as MapTextures['shore'];
  for (const v of SHORE_VARIANTS) {
    shore[v] = {} as Record<ShoreKey, Texture[]>;
    for (const k of SHORE_KEYS)
      shore[v][k] = Array.from({ length: SHORE_FRAMES }, (_, f) => tex[`shore_${v}_${k}_${f}`]);
  }

  const frame = {} as MapTextures['frame'];
  for (const k of SHORE_KEYS) frame[k] = tex[`frame_${k}`];

  const till = { dry: tex['till_dry'], wet: tex['till_wet'] };
  const plot = { dry: tex['plot_dry'], wet: tex['plot_wet'] };
  const overlay = {} as MapTextures['overlay'];
  for (const k of OVERLAY_KEYS) overlay[k] = { dry: tex[`overlay_${k}_dry`], wet: tex[`overlay_${k}_wet`] };
  const inner = {} as MapTextures['inner'];
  for (const k of INNER_KEYS) inner[k] = tex[`inner_${k}`];
  const ring = Array.from({ length: RING_FRAMES }, (_, i) => tex[`ring_${i}`]);

  const mining: Record<string, Texture> = {};
  for (const m of MINING_KEYS) mining[m] = tex[`mining_${m}`];
  const res = {} as MapTextures['res'];
  for (const r of RES_KEYS) res[r] = tex[`res_${r}`];

  return { ground, tree, decor, decorAnim, terrain, fx, water, shore, frame, till, plot, overlay, inner, ring, mining, res };
}
